// A date custom field's popover body on the Opportunities list. The picker only edits a draft: its
// segments are keyboard-editable, it emits a new date as soon as typed segments form one, and it emits
// nothing (undefined) the moment a segment is cleared, so saving on change would store a date the user
// was still typing and silently clear the field.
// Update saves a complete, changed date; Cancel and Escape discard. There is no clear action: a stored date
// can be changed from the list but not emptied. The design-system DatePicker takes and emits ISO YYYY-MM-DD,
// the only format the service accepts.
import React, {useRef, useState} from 'react';
import {Button, DatePicker, EMPHASIS} from '@forecast-it/design-system';
import {isValidIsoDate} from '../../../../shared/util/customFieldValueValidation';
import {Actions, Body} from './editorLayout';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

export const DateFieldEditor = ({node, def, current, onCommit, close, labels}: CustomFieldEditorProps): JSX.Element => {
	const initial = current ?? '';
	const [draft, setDraft] = useState(initial);
	// The popover stays mounted through its exit animation, so a second press could otherwise save again.
	const saving = useRef(false);
	// An emptied picker is a draft on its way to another date, never a value to save.
	const canUpdate = draft !== initial && draft !== '' && isValidIsoDate(draft);
	const update = () => {
		if (!canUpdate || saving.current) return;
		saving.current = true;
		saveCustomFieldValue({node, def, value: draft, onCommit});
		close();
	};
	return (
		<Body data-cy="cf-date-editor">
			<DatePicker
				value={draft || undefined}
				onChange={(iso?: string) => setDraft(iso ?? '')}
				aria-label={def.displayName}
				data-cy="cf-date-input"
			/>
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="cf-cancel" onPress={close}>
					{labels.cancel}
				</Button>
				<Button emphasis={EMPHASIS.HIGH} data-cy="cf-submit" disabled={!canUpdate} onPress={update}>
					{labels.update}
				</Button>
			</Actions>
		</Body>
	);
};

export default DateFieldEditor;
